import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'core/components/button';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { TextField } from 'core/components/text-field';
import { MAX_DOCUMENT_LABEL_LENGTH } from '@ourbranch/lookups';
import useStyles from './rename-document-modal.styles';

/**
 * Modal for setting an editable display label on an uploaded document (e.g.
 * "Barn 1", "House at location 5", "Incorrect photo"). Saving a blank value
 * clears the label so the UI falls back to the original file name.
 * See issue #31290.
 */
const RenameDocumentModal = ({ fileName, currentLabel, onSave, onClose }) => {
  const classes = useStyles();
  const [label, setLabel] = useState(currentLabel || '');
  const [saving, setSaving] = useState(false);

  const trimmed = label.trim();
  const hasChanges = trimmed !== (currentLabel || '').trim();

  const handleSave = async (nextLabel) => {
    setSaving(true);
    try {
      await onSave(nextLabel);
      onClose();
    } catch {
      // onSave surfaces its own error toast; keep the modal open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog className={classes.modal} onClose={onClose} open>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          Rename document
        </Label>
        <Label className={classes.fileName} type="subtitle1Regular">
          {fileName}
        </Label>
        <TextField
          mode="light"
          className={classes.input}
          label="Display name"
          placeholder="e.g. Barn 1, House at location 5, Incorrect photo"
          helperText={`Shown to underwriters and adjusters in place of the file name. ${MAX_DOCUMENT_LABEL_LENGTH} character max.`}
          value={label}
          autoFocus
          disabled={saving}
          inputProps={{ maxLength: MAX_DOCUMENT_LABEL_LENGTH }}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hasChanges && trimmed) {
              handleSave(trimmed);
            }
          }}
        />
        <div className={classes.buttonContainer}>
          <Button
            className={classes.buttons}
            variant="contained"
            color="primary"
            onClick={() => handleSave(trimmed)}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="contained" color="secondary" onClick={onClose} className={classes.buttons} disabled={saving}>
            Cancel
          </Button>
          {currentLabel ? (
            <Button
              className={classes.clearButton}
              variant="text"
              color="secondary"
              onClick={() => handleSave('')}
              disabled={saving}
            >
              Clear label
            </Button>
          ) : null}
        </div>
      </div>
    </BaseDialog>
  );
};

RenameDocumentModal.propTypes = {
  fileName: PropTypes.string.isRequired,
  currentLabel: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

RenameDocumentModal.defaultProps = {
  currentLabel: ''
};

export default RenameDocumentModal;
